import {
  useTranslation
} from "../../../../chunk-6N4NEKWU.mjs";
import "../../../../chunk-D57E6H3M.mjs";

// src/components/composite/Timeline/ProductList/ProductList.tsx
import { useEffect, useRef, useState } from "react";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { staggerDelay, useEntranceStagger } from "../../../../hooks/useEntranceStagger.mjs";
import { useExitPresence } from "../../../../hooks/useExitPresence.mjs";
import { shopifyImageSource } from "../../../../utils/shopifyImageSource.mjs";
import { Button } from "../../../primitive/Button/Button.mjs";
import { StableLabel } from "../../../primitive/Button/StableLabel.mjs";
import { DrawnCheck } from "../../../primitive/DrawnCheck/DrawnCheck.mjs";
import { ImageFrame } from "../../../primitive/ImageFrame/ImageFrame.mjs";
import { Skeleton } from "../../../primitive/Skeleton/Skeleton.mjs";
import { Spinner } from "../../../primitive/Spinner/index.mjs";
import { Text } from "../../../primitive/Text/Text.mjs";
import { resolveProductUrl } from "../ContentChips/productMentions.mjs";
import { AddToCartStatus } from "../shared/AddToCartStatus.mjs";
import { addToCartAriaLabel, useAddToCartState } from "../shared/useAddToCartState.mjs";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var CARD_IMAGE_PX = 120;
function SkeletonCard({ hasAddToCart }) {
  return /* @__PURE__ */ jsxs("div", { className: "buyscout-product-card buyscout-product-card-skeleton", children: [
    /* @__PURE__ */ jsx("div", { className: "buyscout-product-image", children: /* @__PURE__ */ jsx(Skeleton, { variant: "rect", className: "buyscout-product-skeleton-image" }) }),
    /* @__PURE__ */ jsxs("div", { className: "buyscout-product-info", children: [
      /* @__PURE__ */ jsx(Skeleton, { height: 14, className: "buyscout-product-skeleton-title" }),
      /* @__PURE__ */ jsx(Skeleton, { width: "60%", height: 14, className: "buyscout-product-skeleton-title buyscout-product-skeleton-title-line2" }),
      /* @__PURE__ */ jsx(Skeleton, { width: "60%", height: 22, className: "buyscout-product-skeleton-price" }),
      hasAddToCart && /* @__PURE__ */ jsx(Skeleton, { variant: "rect", className: "buyscout-product-skeleton-cart" })
    ] })
  ] });
}
function ProductLinkTitle({ product, storeDomain }) {
  const url = resolveProductUrl(product.url, storeDomain);
  return url ? /* @__PURE__ */ jsx("a", { href: url, target: "_blank", rel: "noopener noreferrer", className: "buyscout-product-link", children: product.title }) : product.title;
}
function ProductList({ products, loading = false, storeDomain, onAddToCart }) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const hadSkeletonsRef = useRef(false);
  if (loading) {
    hadSkeletonsRef.current = true;
  }
  const skipCardAnimation = hadSkeletonsRef.current && !loading && products.length > 0;
  const entranceIndex = useEntranceStagger(products.map((product) => product.id));
  const [showStartGradient, setShowStartGradient] = useState(false);
  const [showEndGradient, setShowEndGradient] = useState(true);
  const { cartStateOf, addToCart, outcome } = useAddToCartState(onAddToCart);
  const trailingRef = useRef(null);
  const indicatorRef = useRef(null);
  const previousRef = useRef(null);
  const nextRef = useRef(null);
  const { rendered: showTrailing, closing: trailingClosing } = useExitPresence(loading || void 0, trailingRef);
  const { rendered: showIndicator, closing: indicatorClosing } = useExitPresence(loading || void 0, indicatorRef);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const updateGradients = () => {
      const { scrollWidth, clientWidth } = container;
      const offset = Math.abs(container.scrollLeft);
      const isAtStart = offset <= 1;
      const isAtEnd = offset >= scrollWidth - clientWidth - 1;
      if (isAtStart && !isAtEnd && previousRef.current?.matches(":focus")) nextRef.current?.focus();
      if (isAtEnd && !isAtStart && nextRef.current?.matches(":focus")) previousRef.current?.focus();
      setShowStartGradient(!isAtStart);
      setShowEndGradient(!isAtEnd);
    };
    updateGradients();
    container.addEventListener("scroll", updateGradients);
    window.addEventListener("resize", updateGradients);
    container.addEventListener("transitionend", updateGradients);
    return () => {
      container.removeEventListener("scroll", updateGradients);
      window.removeEventListener("resize", updateGradients);
      container.removeEventListener("transitionend", updateGradients);
    };
  }, [products, loading]);
  const scrollByPage = (direction) => {
    const container = containerRef.current;
    if (!container) return;
    const { paddingLeft, paddingRight, direction: writing } = getComputedStyle(container);
    const page = container.clientWidth - Number.parseFloat(paddingLeft) - Number.parseFloat(paddingRight);
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const towardEnd = writing === "rtl" ? -1 : 1;
    container.scrollBy({ left: direction * towardEnd * page, behavior: reduced ? "instant" : "smooth" });
  };
  if (!products || products.length === 0) {
    if (!loading) {
      return null;
    }
  }
  const displayProducts = products;
  return /* @__PURE__ */ jsxs("div", { className: "buyscout-product-list", children: [
    /* @__PURE__ */ jsxs(
      "ul",
      {
        ref: containerRef,
        role: "list",
        "aria-label": t("product.listLabel", { count: displayProducts.length }),
        "aria-busy": loading || void 0,
        className: "buyscout-product-list-container",
        children: [
          displayProducts.map((product) => /* @__PURE__ */ jsx("li", { className: "buyscout-product-list-item", "data-arriving": loading || void 0, children: /* @__PURE__ */ jsxs(
            "div",
            {
              className: `buyscout-product-card${skipCardAnimation ? " buyscout-product-card-no-animate" : ""}`,
              style: { "--card-enter-delay": staggerDelay(entranceIndex(product.id)) },
              children: [
                /* @__PURE__ */ jsx(
                  ImageFrame,
                  {
                    ...product.imageUrl ? shopifyImageSource(product.imageUrl, CARD_IMAGE_PX) : void 0,
                    alt: "",
                    fallback: "bag",
                    loading: "lazy",
                    className: "buyscout-product-image",
                    imgClassName: "buyscout-product-image-img"
                  }
                ),
                /* @__PURE__ */ jsxs("div", { className: "buyscout-product-info", children: [
                  /* @__PURE__ */ jsx(Text, { variant: "body-bold", as: "h3", className: "buyscout-product-title", children: /* @__PURE__ */ jsx(ProductLinkTitle, { product, storeDomain }) }),
                  /* @__PURE__ */ jsx("div", { className: "buyscout-product-price-row", children: /* @__PURE__ */ jsx(Text, { variant: "body-bold", as: "span", className: "buyscout-product-price", children: product.price }) }),
                  onAddToCart && (() => {
                    const state = cartStateOf(product.id);
                    const isLoading = state === "loading";
                    const isAdded = state === "added";
                    const isError = state === "error";
                    const addedLabel = /* @__PURE__ */ jsxs(Fragment, { children: [
                      /* @__PURE__ */ jsx(DrawnCheck, { drawn: isAdded }),
                      t("product.added")
                    ] });
                    return /* @__PURE__ */ jsx(
                      Button,
                      {
                        type: "button",
                        variant: "prominent",
                        size: "sm",
                        color: isAdded ? "success" : isError ? "error" : "primary",
                        className: `buyscout-product-add-to-cart buyscout-add-to-cart ${state}`,
                        "data-cart-state": state,
                        onClick: () => {
                          void addToCart(product);
                        },
                        loading: isLoading,
                        "aria-disabled": isAdded || void 0,
                        "aria-label": addToCartAriaLabel(t, state, product.title),
                        children: /* @__PURE__ */ jsx(StableLabel, { labels: [t("product.addToCart"), addedLabel, t("product.failed")], children: isAdded ? addedLabel : isError ? t("product.failed") : t("product.addToCart") })
                      }
                    );
                  })()
                ] })
              ]
            }
          ) }, product.id)),
          showTrailing && /* @__PURE__ */ jsx(
            "li",
            {
              ref: trailingRef,
              className: "buyscout-product-list-trailing",
              "data-state": trailingClosing ? "closed" : "open",
              "aria-hidden": true,
              children: /* @__PURE__ */ jsx(SkeletonCard, { hasAddToCart: !!onAddToCart })
            }
          )
        ]
      }
    ),
    onAddToCart && /* @__PURE__ */ jsx(AddToCartStatus, { outcome, t }),
    /* @__PURE__ */ jsx(
      "div",
      {
        className: `buyscout-product-list-gradient buyscout-product-list-gradient-start ${showStartGradient ? "visible" : ""}`
      }
    ),
    /* @__PURE__ */ jsx(
      "div",
      {
        className: `buyscout-product-list-gradient buyscout-product-list-gradient-end ${showEndGradient ? "visible" : ""}`
      }
    ),
    /* @__PURE__ */ jsx(
      Button,
      {
        ref: previousRef,
        variant: "glass",
        color: "neutral",
        shape: "circle",
        size: "sm",
        iconOnly: true,
        "aria-label": t("product.scrollPrevious"),
        className: "buyscout-product-list-arrow buyscout-product-list-arrow--previous",
        disabled: !showStartGradient,
        onClick: () => scrollByPage(-1),
        children: /* @__PURE__ */ jsx(MdChevronLeft, { "aria-hidden": true })
      }
    ),
    /* @__PURE__ */ jsx(
      Button,
      {
        ref: nextRef,
        variant: "glass",
        color: "neutral",
        shape: "circle",
        size: "sm",
        iconOnly: true,
        "aria-label": t("product.scrollNext"),
        className: "buyscout-product-list-arrow buyscout-product-list-arrow--next",
        disabled: !showEndGradient,
        onClick: () => scrollByPage(1),
        children: /* @__PURE__ */ jsx(MdChevronRight, { "aria-hidden": true })
      }
    ),
    showIndicator && /* @__PURE__ */ jsx(
      "div",
      {
        ref: indicatorRef,
        className: "buyscout-product-list-loading-indicator",
        "data-state": indicatorClosing ? "closed" : "open",
        "aria-hidden": true,
        children: /* @__PURE__ */ jsx(Spinner, { size: "sm" })
      }
    )
  ] });
}
export {
  ProductList
};
//# sourceMappingURL=ProductList.mjs.map